import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { Diamond, PeerBand } from '../api/types'
import { peerFor, peerSentence, peerSpoken, peerExplainer, type PeerPosition } from '../lib/peerBands'
import { staggerDelayMs } from '../lib/stagger'
import './stonePeer.css'

/**
 * THE PEER MARK — where this stone's price sits in its own market.
 *
 * ── WHY THIS EXISTS ───────────────────────────────────────────────────────
 * A stone list answers two questions: which of these, and is the price right.
 * The second one was, until now, unanswerable on this surface. "$8,500/ct" is
 * not a decision; "$8,500/ct, 12% under 61 comparable" is. VDB — the reference
 * for this whole screen — is a database front-end: it can show you a COLUMN of
 * prices, it cannot show you a POSITION. We hold the entire catalog in memory
 * in `api` and rebuild a facet artifact every ten minutes, so a position costs
 * one aggregate over data we already have.
 *
 * ── WHAT IT IS NOT ────────────────────────────────────────────────────────
 * It is not a valuation. The copy says "under the N comparable stones on
 * Diaex" and never "below market", never "deal", never "value" — those are
 * claims about the global trade that we cannot defend and that counsel has not
 * cleared (#29). The denominator is always stated out loud.
 *
 * ── HOW IT FAILS ──────────────────────────────────────────────────────────
 * Silently, and completely. No band, an under-quorum bucket, a locked price,
 * an unverified viewer (the server withholds the bands entirely) → this
 * renders `null`. Never a dash, never a skeleton, never "insufficient data" —
 * the browse surface's standing rule is that it does not print an em-dash for
 * something it does not hold, and a fabricated position is the single most
 * damaging thing this screen could say.
 */

/**
 * The bands for the current result set, provided once at the page level.
 *
 * A context rather than a prop drilled through four view components: the table
 * row, the card, and both of their mobile twins all need it, and threading a
 * map through `DiamondTable → Row → cell` would put a new required prop on
 * every memoized component (and bust their memoization on every re-render,
 * which is the exact lag bug DiamondTable.test.tsx exists to prevent).
 * Undefined is the normal state for an unverified viewer.
 */
const PeerContext = createContext<Record<string, PeerBand> | undefined>(undefined)

export function PeerBandsProvider({ bands, children }: { bands?: Record<string, PeerBand>; children: ReactNode }) {
  return <PeerContext.Provider value={bands}>{children}</PeerContext.Provider>
}

/** The band map for the current results, or undefined. */
export function usePeerBands(): Record<string, PeerBand> | undefined {
  return useContext(PeerContext)
}

/** This stone's position, memoized on the inputs that can actually change it. */
export function usePeer(stone: Diamond, mult = 1): PeerPosition | null {
  const bands = usePeerBands()
  return useMemo(() => peerFor(stone, bands, mult), [stone, bands, mult])
}

/**
 * The mark itself.
 *
 * `variant="row"` is the dense form — a 40px track and nothing else. Its
 * meaning is carried by the screen-reader sentence and by the row expansion,
 * because a table row has no vertical space for a sentence and stuffing one in
 * would cost the table a column it does not have.
 *
 * `variant="card"` adds the sentence. A card has the room, and a card grid is
 * the browse-y view where the buyer is not yet scanning a spreadsheet.
 *
 * `index` drives the entrance stagger via lib/stagger's cap rule.
 */
export function PriceMark({
  stone,
  peer,
  variant = 'row',
  index = 0,
  className = '',
  trackWidth,
}: {
  stone: Diamond
  /** Pass a precomputed position to avoid re-deriving it in a hot list. */
  peer: PeerPosition | null
  variant?: 'row' | 'card'
  index?: number
  className?: string
  /** Track width in px. Defaults per variant; card stretches to its container. */
  trackWidth?: number
}) {
  if (!peer) return null

  const width = trackWidth != null ? `${trackWidth}px` : variant === 'card' ? '100%' : '40px'
  // Capped stagger: item 12 and item 49 share a delay, so a 50-row page lands
  // as a wave followed by a block rather than a 2.7-second crawl.
  const delay = staggerDelayMs(index, { step: 18, cap: 20 })

  return (
    <span
      className={`peer-mark ${className}`}
      data-testid="peer-mark"
      data-read={peer.read}
      data-variant={variant}
      data-n={peer.n}
    >
      {/* The track is decoration; every bit of meaning is in the sentence
          below it. A screen reader hearing "12 percent under the median of 61
          comparable stones" is strictly better served than by any description
          of a bar, which is why the bar is hidden outright. */}
      <span
        className="peer-track"
        aria-hidden="true"
        style={{ '--track-w': width } as React.CSSProperties}
      >
        <span
          className="peer-tick"
          style={{ '--pos': peer.pos, '--tick-delay': `${delay}ms`, '--track-w': width } as React.CSSProperties}
        />
      </span>
      {variant === 'card' && (
        <span className="peer-read" aria-hidden="true">{peerSentence(peer)}</span>
      )}
      <span className="sr-only">
        {`Price for stone ${stone.stockId}: ${peerSpoken(peer)}`}
      </span>
    </span>
  )
}

/**
 * The explanation, for an expanded row or a detail sheet. This is where the
 * mark stops being a glyph and states its denominator in full — the group it
 * compared against and what the middle of that group costs.
 */
export function PeerExplainer({ peer, className = '' }: { peer: PeerPosition | null; className?: string }) {
  if (!peer) return null
  const text = peerExplainer(peer)
  // Split on the median so the number can carry ink weight without a second
  // string to keep in sync with lib/peerBands.
  const [head, median] = text.split(/Median /)
  return (
    <p className={`peer-explainer ${className}`} data-testid="peer-explainer">
      {head}Median <strong>{median?.replace(/\.$/, '')}</strong>.
    </p>
  )
}
